/**
 * HybridDriftManagementTab — drift queue for tenants whose
 * `baselineSource === 'Hybrid'`.
 *
 * Wave 7 of the TCM migration. Renders TCM drifts (Entra/EXO/Intune/Teams/
 * Defender/Purview) merged with DSC drifts (SPO + OneDrive, permanent
 * steady state) in one unified table. DSC-only tenants continue to render
 * `DriftManagementTab` byte-for-byte — see `ConfigurationDrift.tsx` for
 * the branch.
 *
 * Two parallel queries:
 *   - `api.getConfigurationDrifts({ ..., sortBy: 'severity' })`
 *   - `tcmApi.listDrifts(tenantId, { state: 'Active', sortBy: 'severity' })`
 *
 * Both endpoints take `sortBy=severity` (added by Slice A backend work) so
 * page 1 from each source genuinely contains the highest-severity rows —
 * see blueprint §11 R-NEW-5 for why this matters. The client-side
 * `compareUnifiedRows` is only the cross-source tiebreaker.
 *
 * Carbon (mockup 02): an action row (where the queue comes from, Refresh,
 * the triage trigger and Apply), the queue notices, then the Drifts
 * DataTable — Open / Waiting to apply / Back to baseline status tabs, a
 * toolbar (search, Workload and Severity selects, the audit export), the
 * Soft Green batch bar, resources with several drifted settings grouped
 * under one header, and rows that expand in place into the drift detail
 * (D25). No fabricated metrics — if a TCM field is null we show "—" or omit.
 */

import { useMemo, useState, type ReactNode } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowPathIcon } from '@heroicons/react/24/outline'

import { api, tcmApi } from '@/lib/api'
import type { DriftTriageRunTally, TcmBulkWaiveResult } from '@/lib/api'
import { getServerMessage } from '@/lib/api-error'
import {
  isCreationDetectorRow,
  creationDetectorCopy,
  isDeletionDetectorRow,
  deletionDetectorCopy,
  isAcknowledgeRow,
  acknowledgeCopy,
  isUpgradeIntroducedRow,
  upgradeIntroducedCopy,
  foldErrorCopyFromError,
  sharedTriageSuggestion,
  triageSuggestionCopy,
  isSpoOdbComponent,
} from '@/lib/drift-decision-copy'
import TriagePill from '@/components/Drift/TriagePill'
import AcceptSuggestionModal from '@/components/Drift/AcceptSuggestionModal'
import type { TcmStageResult, TcmDriftSummary } from '@/types/tcm'
import { composeSeverityProvenance } from '@/lib/severity-provenance'
import { DriftStatus } from '@/types'
import {
  applyAllStaged,
  classifyApplyOutcome,
  summarizeBulkStage,
  unsupportedStageCopy,
  isUnstageNoop,
  stagedBannerBody,
} from '@/lib/drift-staging'
import { useTenantContext } from '@/hooks/useTenantContext'
import {
  normalizeToUnifiedRow,
  normalizeTcmToUnifiedRow,
  compareUnifiedRows,
  type UnifiedDriftRow,
  type DriftSource,
} from '@/types/unifiedDrift'
import { displayStateLabel, type DriftDisplayState } from '@/lib/drift-lifecycle'
import { canChangeDrifts } from '@/lib/drift-permissions'
import DriftDetailDrawer, { DriftDetailContent } from '@/components/Drift/DriftDetailDrawer'
import DriftAuditExportModal from '@/components/Drift/DriftAuditExportModal'
import BulkActionCommentModal from '@/components/Drift/BulkActionCommentModal'
import {
  prettifyResourceType,
  workloadLabelForResourceType,
} from '@/lib/tcmResourceNames'
import { driftResourceName, dscResourceLabel, settingLabel } from '@/lib/drift-names'
import { priorityShiftCopy, priorityShiftGroups } from '@/lib/drift-priority-shift'
import { Button } from '@/components/carbon/Button'
import { ContainedTabs } from '@/components/carbon/ContainedTabs'
import { DataTable, TableItem, type DataColumn, type DataTableGroups } from '@/components/carbon/DataTable'
import type { BatchAction } from '@/components/carbon/BatchActionBar'
import { IconButton } from '@/components/carbon/IconButton'
import { IconCheck, IconDownload } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { TableSearch } from '@/components/carbon/TableSearch'
import { TableSelect } from '@/components/carbon/TableSelect'
import { Tag } from '@/components/carbon/Tag'
import { Tooltip } from '@/components/carbon/Tooltip'
import { ChangeCell, RowToggleTitle, WhenCell } from '@/components/BaselineCompliance/drift-carbon'
import {
  QUEUE_NOT_ASSESSED,
  SEVERITY_RANK,
  collectPages,
  driftSeverityLevel,
  severityFilterOptions,
  toggleId,
  type SeverityCounts,
} from '@/components/BaselineCompliance/drift-carbon-helpers'

// Active-drift status set mirrors the DSC tab. Remediated drifts live in
// the archive — Hybrid v1 only renders Active.
const DSC_ACTIVE_STATUSES = 'New,Pending,PendingApproval,AwaitingRemediation'

// A staged DSC row is one the user has approved but not yet committed — it
// sits at Status=Approved with ProcessedAt=null until "Apply" opens the PR.
// Mirrors DriftManagementTab's queued-approvals query.
const DSC_STAGED_STATUS = 'Approved'

// Cross-source selection key. DSC and TCM ids are independent namespaces, so
// we qualify by source to avoid an (astronomically unlikely) id collision and
// to recover the source when fanning a bulk action out per backend.
function selectionKey(source: DriftSource, id: string): string {
  return `${source}:${id}`
}

// Page size per source. Both queries pull 50 rows of the top severity —
// the merged view comfortably fits the highest-priority work without
// cross-source cursor pagination (deferred per blueprint §13).
const PAGE_SIZE = 50
// Back to baseline walks DSC history in larger pages, bounded at 2,000 rows.
const DSC_BACK_PAGE_SIZE = 200
const DSC_BACK_MAX_PAGES = 10

export interface HybridDriftManagementTabProps {
  /** Selected tenant from the parent page. Required for both queries. */
  tenantId: string | undefined
  /**
   * Controlled-mode selection — same shape DriftManagementTab uses so the
   * page's Top-items section can open the same drawer instance. The id
   * is the unified row id (DSC drift id OR TCM drift PK depending on
   * which row was clicked).
   */
  selectedDriftId?: string | null
  /**
   * Wave 7 — source discriminator passed by the parent when the controlled
   * `selectedDriftId` came from a Hybrid-aware source (e.g., Slice C's
   * GetCriticalDriftAsync UNION feeding a Top Items row). Lets the drawer
   * call the right audit-history endpoint even before the per-source list
   * queries resolve. When omitted, the existing fallback chain
   * (selectedRow.source → internalSelected.source → 'DSC') applies.
   */
  selectedDriftSource?: 'DSC' | 'TCM'
  onSelectDrift?: (id: string | null, sourceHint?: string) => void
  /** Nothing scanned or the stats call failed: the status tabs show no counts rather than zeros. */
  countsUnknown?: boolean
  /** Nothing scanned: the empty, unfiltered Open view says there is no baseline data yet. */
  unscanned?: boolean
}

interface SelectedRow {
  id: string
  source: DriftSource
}

/**
 * A resource's drifted settings roll up under one group header in the queue:
 * a resource with many drifted properties (e.g. SPOTenantSettings after a DSC
 * 1.26 upgrade) would otherwise flood the list with one row per setting.
 */
function resourceKey(row: UnifiedDriftRow): string {
  return `${row.source}:${row.resourceType}:${row.resourceName ?? row.resourceType}`
}

/** Severity select — client-side over the loaded merged set. */
type SeverityFilter = 'All' | 'Critical' | 'High' | 'Medium' | 'Low'

/** The status tabs: the open queue, accepted changes waiting to apply, and drift back to baseline. */
type StatusTab = 'open' | 'accepted' | 'back'
/** Each tab's own display state; a row in any other state names it in its When cell. */
const TAB_STATE: Record<StatusTab, DriftDisplayState> = { open: 'open', accepted: 'staged', back: 'resolved' }

export default function HybridDriftManagementTab({
  tenantId,
  selectedDriftId: controlledSelectedDriftId,
  selectedDriftSource,
  onSelectDrift,
  countsUnknown = false,
  unscanned = false,
}: HybridDriftManagementTabProps) {
  const { userTenants } = useTenantContext()
  const queryClient = useQueryClient()
  const [, setSearchParams] = useSearchParams()
  const [internalSelected, setInternalSelected] = useState<SelectedRow | null>(null)
  const [showAuditExportModal, setShowAuditExportModal] = useState(false)

  // Cross-source multi-select. Keys are `${source}:${id}` so DSC + TCM rows
  // co-exist in one set; "Accept N" fans the selection out per backend.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  // Controlled row expansion (the same keys), so a row's title toggles its detail as the chevron does.
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])

  // Bulk-accept now routes through a comment modal (parity with the DSC tab's
  // BulkActionCommentModal) so a batch — e.g. adopting a wave of DSC-upgrade
  // "New setting" SPO rows — can carry one audit note. Comment stays optional.
  const [showBulkCommentModal, setShowBulkCommentModal] = useState(false)

  // Drift Triage Copilot: the "Accept Guardian's suggestion" evidence modal.
  const [showAcceptSuggestionModal, setShowAcceptSuggestionModal] = useState(false)

  // Platform-Admin gate for the "Ask Guardian to triage this queue" trigger.
  // Same query key + fetcher the shell and useTenantContext use, so it rides
  // the shared cache instead of a fresh /me round-trip. Non-PA users never see
  // the trigger (a failed/absent fetch reads as non-PA — secure by default).
  const currentUserQuery = useQuery({
    queryKey: ['currentUser'],
    queryFn: api.getCurrentUser,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
  const isPlatformAdmin = currentUserQuery.data?.isPlatformAdmin === true
  // Viewers can read the queue but not act on it (the server 403s every drift
  // write below Approver), so selection, bulk accept, Apply, Undo and the
  // drawer's Accept / Reject are hidden for them.
  const canWrite = canChangeDrifts(currentUserQuery.data)

  // Severity chips filter the visible queue client-side (the loaded set is
  // each source's top-50 by severity). Changing the filter clears the
  // selection so the bulk bar can never act on rows the user can't see.
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('All')
  // WO-3: when true, schema-noise (non-RealChange) rows join the queue. Default
  // false — the customer sees only real changes. Toggled by the "N schema-noise
  // items filtered" affordance in the table footer.
  const [includeFiltered, setIncludeFiltered] = useState(false)
  function applySeverityFilter(next: SeverityFilter) {
    setSeverityFilter(next)
    setSelectedKeys(new Set())
  }
  // The toolbar's search and Workload select narrow the loaded set the same
  // way, and clear the selection for the same reason.
  const [search, setSearch] = useState('')
  const [workloadFilter, setWorkloadFilter] = useState('')
  function applySearch(next: string) {
    setSearch(next)
    setSelectedKeys(new Set())
  }
  function applyWorkloadFilter(next: string) {
    setWorkloadFilter(next)
    setSelectedKeys(new Set())
  }
  const [statusTab, setStatusTab] = useState<StatusTab>('open')
  function applyStatusTab(next: string) {
    setStatusTab(next as StatusTab)
    setSelectedKeys(new Set())
  }

  // Controlled vs uncontrolled selection — same pattern DriftManagementTab
  // uses. When controlled, source is unknown to the parent so we keep it
  // in local state alongside the id.
  const isControlled = controlledSelectedDriftId !== undefined

  // ── Queries (parallel) ─────────────────────────────────────────────
  const dscQuery = useQuery({
    // WO-3: includeFiltered is part of the key so toggling refetches the queue
    // with (or without) schema-noise rows.
    queryKey: ['hybrid-drift-dsc', tenantId, includeFiltered],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId,
        status: DSC_ACTIVE_STATUSES,
        page: 1,
        limit: PAGE_SIZE,
        sortBy: 'severity',
        includeFiltered,
      }),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })

  const tcmQuery = useQuery({
    queryKey: ['hybrid-drift-tcm', tenantId, includeFiltered],
    queryFn: () =>
      tcmApi.listDrifts(tenantId!, {
        state: 'Active',
        page: 1,
        pageSize: PAGE_SIZE,
        sortBy: 'severity',
        includeFiltered,
      }),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })

  // Tier-naming follow-up (Adam, 2026-09-23): the split-line's "Continuous
  // watch" / "Full capture" lane counts need an honest server-side total per
  // Source, not a count over the one loaded page above — a busy tenant's
  // Tier-2 sweep can run into the dozens while the queue only loads its
  // top-50-by-severity page. Same pattern as the WO-3 schema-noise count
  // probes below: cheap pageSize:1 calls that read `totalCount`. Same
  // `includeFiltered` dependency as tcmQuery so toggling schema-noise
  // visibility keeps the lane counts in sync with what the queue shows.
  const tcmContinuousCountQuery = useQuery({
    queryKey: ['hybrid-drift-tcm-continuous-count', tenantId, includeFiltered],
    queryFn: () =>
      tcmApi.listDrifts(tenantId!, {
        state: 'Active',
        page: 1,
        pageSize: 1,
        includeFiltered,
        excludeSource: 'SnapshotDiff',
      }),
    enabled: !!tenantId,
    // Keep the prior total on screen while the includeFiltered toggle
    // refetches, matching tcmQuery above — without this the lane flashes
    // "0" (no data yet) on every toggle and on first paint.
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })

  const tcmFullCaptureCountQuery = useQuery({
    queryKey: ['hybrid-drift-tcm-fullcapture-count', tenantId, includeFiltered],
    queryFn: () =>
      tcmApi.listDrifts(tenantId!, {
        state: 'Active',
        page: 1,
        pageSize: 1,
        includeFiltered,
        source: 'SnapshotDiff',
      }),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })

  // Coverage/onboarding overlay — drives the "mail coverage is still finishing"
  // banner (RS-03) for a tenant in the transient ExoProvisioning state: Graph
  // coverage is live but Exchange RBAC hasn't finished setting up yet. Read-only;
  // a failed/absent fetch simply omits the banner. (Sequenced-require retired the
  // D2 "Exchange deferred by default" model — mail is required, not optional.)
  const coverageQuery = useQuery({
    queryKey: ['tcm-coverage', tenantId],
    queryFn: () => tcmApi.getCoverage(tenantId!),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
  })
  const mailCoverageIncomplete = coverageQuery.data?.onboarding?.exoProvisioning === true

  // WO-3: two cheap (limit:1) count probes drive the "N schema-noise items
  // filtered" affordance. Independent of the display toggle so the count stays
  // stable whether or not noise is currently shown. Noise is DSC-only in phase 1
  // (all TCM rows are RealChange), so a single DSC real-vs-all delta is exact.
  const dscRealCountQuery = useQuery({
    queryKey: ['hybrid-drift-dsc-realcount', tenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId,
        status: DSC_ACTIVE_STATUSES,
        page: 1,
        limit: 1,
      }),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
  })

  const dscAllCountQuery = useQuery({
    queryKey: ['hybrid-drift-dsc-allcount', tenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId,
        status: DSC_ACTIVE_STATUSES,
        page: 1,
        limit: 1,
        includeFiltered: true,
      }),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
  })

  const noiseCount = Math.max(
    0,
    (dscAllCountQuery.data?.total ?? 0) - (dscRealCountQuery.data?.total ?? 0),
  )

  // ── Staged-queue queries (Accept-via-Staging) ──────────────────────
  // Staged rows have left the Active view (TCM AcceptedPending and DSC
  // Approved are both excluded from the Active queries above), so we load
  // them separately to drive the Staged banner, the staged count, and the
  // per-row Staged pill + Undo. Both pull the full staged set (it's small —
  // a tenant only stages a handful between Applies); the TCM TotalCount is
  // the authoritative staged count even if the page is capped.
  const tcmStagedQuery = useQuery({
    queryKey: ['hybrid-drift-tcm-staged', tenantId],
    queryFn: () =>
      tcmApi.listDrifts(tenantId!, {
        state: 'AcceptedPending',
        page: 1,
        pageSize: PAGE_SIZE,
      }),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })

  const dscStagedQuery = useQuery({
    queryKey: ['hybrid-drift-dsc-staged', tenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId,
        status: DSC_STAGED_STATUS,
        unprocessedOnly: true,
        page: 1,
        limit: PAGE_SIZE,
      }),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })

  // ── Back to baseline (blueprint §7 B4) ────────────────────────────
  // Drift the tenant brought back in line, from both sources. For TCM a cheap
  // pageSize:1 probe gives the tab its count; the rows load only while the
  // tab is open. Read-only: nothing to decide on a remediated row.
  //
  // DSC is different: the TCM cutover (DscSupersessionService) closed every
  // open non-SharePoint/OneDrive DSC drift as Remediated without it being
  // fixed, and the list endpoint has no Hybrid scope. So the DSC page always
  // loads, is filtered to SPO/ODB components (the Wave9Scope mirror), and the
  // tab counts those loaded rows rather than the unscoped server `total`.
  const backListOpen = statusTab === 'back'
  const backPageSize = backListOpen ? PAGE_SIZE : 1
  const tcmBackQuery = useQuery({
    queryKey: ['hybrid-drift-tcm-back', tenantId, backPageSize],
    queryFn: () =>
      tcmApi.listDrifts(tenantId!, {
        state: 'Remediated',
        page: 1,
        pageSize: backPageSize,
        sortBy: 'severity',
      }),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })
  const dscBackQuery = useQuery({
    queryKey: ['hybrid-drift-dsc-back', tenantId],
    queryFn: async () => {
      // Walk the remediated history (bounded) so SPO/ODB rows are not lost
      // behind the cutover-superseded rows that sort ahead of them.
      const { items, total } = await collectPages(
        page =>
          api.getConfigurationDrifts({
            tenantId,
            status: 'Remediated',
            page,
            limit: DSC_BACK_PAGE_SIZE,
            sortBy: 'severity',
          }),
        DSC_BACK_MAX_PAGES,
      )
      return { items, total, page: 1, limit: items.length }
    },
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })
  const dscBackItems = useMemo(
    () => (dscBackQuery.data?.items ?? []).filter(d => isSpoOdbComponent(d.component)),
    [dscBackQuery.data?.items],
  )

  // Staged counts. Both totals are server-side authoritative (real row counts
  // even when the loaded page is capped). Defined before the mutations so the
  // Apply mutationFn closes over a defined value.
  //
  // F1/F8 — an errored staged query MUST NOT coerce to a 0 count. A 0 from a
  // failed query is a lie: the staged queue may exist but we couldn't read it,
  // and a 0 would silently hide the banner, the Apply button, the Staged
  // section, and Undo — and (worse) let an asymmetric apply fire a DSC-only
  // batch that strands TCM staged rows. We track the error explicitly and gate
  // every staged affordance on neither staged query being errored.
  const tcmStagedError = tcmStagedQuery.isError
  const dscStagedError = dscStagedQuery.isError
  const stagedQueueUnavailable = tcmStagedError || dscStagedError
  const tcmStagedCount = tcmStagedQuery.data?.totalCount ?? 0
  const dscStagedCount = dscStagedQuery.data?.total ?? 0
  // Real staged total — only meaningful when both queries resolved. While
  // either is errored we don't render a count at all (see stagedQueueUnavailable
  // gating below) so this never drives UI in that state.
  const stagedCount = tcmStagedCount + dscStagedCount
  // The Apply affordance + per-source apply gating are hard-disabled while the
  // staged queue is unreadable — we can't honestly say what's staged, and a
  // partial apply could strand the unreadable source's rows.
  const canApply = !stagedQueueUnavailable && stagedCount > 0

  // ── Query invalidation ─────────────────────────────────────────────
  // After any write we refresh every queue this tab reads — both Active lists
  // AND both staged lists — because a single accept moves a row between them.
  // The drawer keys its TCM detail / audit queries off the same tenant; we
  // invalidate those too so an open drawer reflects the new state. (The drawer
  // keys the audit query as 'tcm-drift-audit-history'; the Lab uses
  // 'tcm-drift-audit' — invalidate both so the trail refreshes either way.)
  function invalidateAllDriftQueries(tcmDriftId?: string) {
    void queryClient.invalidateQueries({ queryKey: ['hybrid-drift-dsc', tenantId] })
    void queryClient.invalidateQueries({ queryKey: ['hybrid-drift-tcm', tenantId] })
    // Tier-naming split-line count probes — a stage/reject/apply moves a row
    // out of Active, so the per-lane totals need refreshing alongside the
    // queue itself.
    void queryClient.invalidateQueries({ queryKey: ['hybrid-drift-tcm-continuous-count', tenantId] })
    void queryClient.invalidateQueries({ queryKey: ['hybrid-drift-tcm-fullcapture-count', tenantId] })
    void queryClient.invalidateQueries({ queryKey: ['hybrid-drift-tcm-staged', tenantId] })
    void queryClient.invalidateQueries({ queryKey: ['hybrid-drift-dsc-staged', tenantId] })
    // DSC's own tab keys (so switching tabs doesn't show a stale queue).
    void queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
    void queryClient.invalidateQueries({ queryKey: ['queued-approvals'] })
    if (tcmDriftId) {
      void queryClient.invalidateQueries({ queryKey: ['tcm-drift-detail', tenantId, tcmDriftId] })
      void queryClient.invalidateQueries({ queryKey: ['tcm-drift-audit-history', tenantId, tcmDriftId] })
      void queryClient.invalidateQueries({ queryKey: ['tcm-drift-audit', tenantId, tcmDriftId] })
      void queryClient.invalidateQueries({ queryKey: ['configuration-drift', tcmDriftId] })
    }
  }

  // ── Per-row Accept (stage) — both sources ──────────────────────────
  // Accept is now an instant, cheap stage. TCM → State=AcceptedPending (via
  // approveTcmDrift, which returns the tenant's staged count); DSC →
  // Status=Approved,ProcessedAt=null (via approveDrift). Neither re-baselines
  // — the expensive batched write waits for the Apply button below. Server
  // messages (404 / 409 / 400) surface verbatim via getServerMessage.
  const tcmApproveMutation = useMutation<
    TcmStageResult,
    Error,
    { driftId: string; comment?: string }
  >({
    mutationFn: ({ driftId, comment }) =>
      tcmApi.approveTcmDrift(tenantId!, driftId, comment || undefined),
    onSuccess: (_result, vars) => {
      // Staged, not applied — name what happened plainly so the user knows
      // the live tenant config hasn't moved yet (Apply does that).
      toast.success('Change staged. Apply when you are ready.')
      invalidateAllDriftQueries(vars.driftId)
      // The staged row leaves Active, so close the drawer back to the queue.
      handleDrawerClose()
    },
    onError: err => {
      // Same server refusal as bulk stage (StageDriftsAsync) — keep its raw
      // developer-voiced body (tenant id, drift GUIDs) out of the toast.
      const serverDetail = getServerMessage(err, 'Could not stage that change. Try again, or check the server log.')
      toast.error(unsupportedStageCopy(serverDetail) ?? serverDetail)
    },
  })

  const tcmRejectMutation = useMutation<
    void,
    Error,
    { driftId: string; comment?: string }
  >({
    mutationFn: ({ driftId, comment }) =>
      tcmApi.rejectTcmDrift(tenantId!, driftId, comment || undefined),
    onSuccess: (_void, vars) => {
      toast.success('Drift dismissed. Reason recorded.')
      invalidateAllDriftQueries(vars.driftId)
      handleDrawerClose()
    },
    onError: err => {
      toast.error(getServerMessage(err, 'Reject did not land. Try again, or check the server log.'))
    },
  })

  const dscApproveMutation = useMutation<
    unknown,
    Error,
    { driftId: string; comment?: string }
  >({
    mutationFn: ({ driftId, comment }) => api.approveDrift(driftId, comment || undefined),
    onSuccess: () => {
      toast.success('Change staged. Apply when you are ready.')
      invalidateAllDriftQueries()
      handleDrawerClose()
    },
    onError: err => {
      toast.error(getServerMessage(err, 'Could not stage that change. Try again, or check the server log.'))
    },
  })

  const dscRejectMutation = useMutation<
    unknown,
    Error,
    { driftId: string; comment?: string }
  >({
    mutationFn: ({ driftId, comment }) => api.rejectDrift(driftId, comment || undefined),
    onSuccess: () => {
      toast.success('Drift dismissed. Reason recorded.')
      invalidateAllDriftQueries()
      handleDrawerClose()
    },
    onError: err => {
      toast.error(getServerMessage(err, 'Reject did not land. Try again, or check the server log.'))
    },
  })

  // ── Cross-source bulk Accept (stage N) ─────────────────────────────
  // The bulk bar fans the selection out per backend in one call each — DSC
  // bulkApproveDrifts + TCM bulkApproveTcmDrifts — instead of N HTTP calls.
  // Whichever source has selected ids gets a call; both run in parallel and
  // are caught independently so a failure on one still stages the other.
  // The bulk result the mutationFn hands onSuccess. We capture per-source
  // outcomes — DSC's per-row failedIds (a 403/404 row fails while siblings
  // stage), whether each source's call threw, and the raw error objects so the
  // toast can surface a real server message (F2/F5). Nothing is swallowed.
  interface BulkAcceptOutcome {
    dscIds: string[]
    tcmIds: string[]
    dscFailedIds: string[]
    dscThrew: boolean
    tcmThrew: boolean
    sourceErrors: { source: 'TCM' | 'DSC'; error: unknown }[]
  }

  const bulkAcceptMutation = useMutation<
    BulkAcceptOutcome,
    Error,
    { dscIds: string[]; tcmIds: string[]; comment?: string }
  >({
    mutationFn: async ({ dscIds, tcmIds, comment }) => {
      const sourceErrors: { source: 'TCM' | 'DSC'; error: unknown }[] = []
      let dscFailedIds: string[] = []
      let dscThrew = false
      let tcmThrew = false
      await Promise.all([
        dscIds.length
          ? api
              .bulkApproveDrifts(dscIds, comment)
              // Per-row truth: the server stages each id independently, so a
              // partial failure (some rows 403/404) still resolves. Keep the
              // failed ids so onSuccess can re-select exactly those.
              .then(res => {
                dscFailedIds = res.failedIds
              })
              .catch((error: unknown) => {
                dscThrew = true
                sourceErrors.push({ source: 'DSC', error })
              })
          : Promise.resolve(),
        tcmIds.length
          ? tcmApi
              .bulkApproveTcmDrifts(tenantId!, tcmIds, comment)
              .catch((error: unknown) => {
                tcmThrew = true
                sourceErrors.push({ source: 'TCM', error })
              })
          : Promise.resolve(),
      ])
      return { dscIds, tcmIds, dscFailedIds, dscThrew, tcmThrew, sourceErrors }
    },
    onSuccess: outcome => {
      // Pure reconciliation: who staged, who didn't, and which keys to keep
      // selected for a retry (TCM all-or-nothing; DSC per-row).
      const verdict = summarizeBulkStage({
        dsc: {
          requested: outcome.dscIds,
          failedIds: outcome.dscFailedIds,
          threw: outcome.dscThrew,
        },
        tcm: {
          requested: outcome.tcmIds,
          failedIds: [],
          threw: outcome.tcmThrew,
        },
      })

      if (verdict.allStaged) {
        // Name the subset that won't touch the baseline, computed from the ids
        // actually submitted in this action (not the live selection, which is
        // cleared below) so a stale re-render can't skew the count.
        const acknowledgedCount = outcome.tcmIds.filter(id => acknowledgeIds.has(id)).length
        const acknowledgedNote =
          acknowledgedCount > 0
            ? ` ${acknowledgedCount === 1 ? 'One of these is' : `${acknowledgedCount} of these are`} acknowledged, not written to the baseline.`
            : ''
        toast.success(
          `${pluralizeChange(verdict.totalStaged)} staged. Apply when you are ready.${acknowledgedNote}`,
        )
        setSelectedKeys(new Set())
      } else {
        // Log the raw error(s) with source context — never swallowed.
        outcome.sourceErrors.forEach(e => {
          console.error(`[drift-staging] ${e.source} bulk-stage failed`, e.error)
        })
        // Surface the truth: actual staged vs requested, and (when a source
        // threw) its server message. Lead with a thrown source's message since
        // it carries the most signal; otherwise name the partial DSC shortfall.
        const threwError = outcome.sourceErrors[0]
        const serverDetail = threwError
          ? getServerMessage(
              threwError.error,
              'Could not stage those changes. Try again, or check the server log.',
            )
          : null
        // A stale selection can still reach the server with a row whose
        // DriftType the SnapshotDiff allowlist rejects (rare post-#95-B —
        // Added/Modified now acknowledge cleanly and Removed retires, so
        // this only fires for a malformed/future row); the 409 body is
        // developer-voiced (tenant id, drift GUIDs, resource types,
        // "snapshot-diff"), so translate it before it hits a toast.
        // Everything else surfaces verbatim.
        const detail =
          serverDetail !== null
            ? (unsupportedStageCopy(serverDetail) ?? serverDetail)
            : `${pluralizeChange(verdict.totalStaged)} staged of ${verdict.totalRequested}. The rest did not stage. They are still selected.`
        toast.error(
          verdict.totalStaged > 0
            ? `Staged ${verdict.totalStaged} of ${verdict.totalRequested}. ${detail}`
            : detail,
          { duration: 6000 },
        )
        // Keep exactly the failed source/rows selected so a retry re-sends only
        // what's outstanding.
        setSelectedKeys(new Set(verdict.keepSelectedKeys))
      }
      invalidateAllDriftQueries()
    },
    onError: err => {
      toast.error(getServerMessage(err, 'Could not stage those changes. Try again, or check the server log.'))
    },
  })

  // ── Undo a staged row ──────────────────────────────────────────────
  // TCM → unstageTcmDrifts (AcceptedPending → Active). DSC has no dedicated
  // unstage route; reverting the Approved row to New is the equivalent — it
  // drops it back into the Active queue without opening a PR.
  //
  // F6 — for TCM the server returns `revertedCount` (rows actually flipped
  // back). A row already applied or rejected elsewhere no-ops and is NOT
  // counted, so revertedCount < 1 here means the Undo did nothing — we must
  // say so rather than claim "pulled that one back". DSC's updateDriftStatus
  // returns no count, so the DSC branch keeps the plain success copy.
  const undoStageMutation = useMutation<
    { source: DriftSource; revertedCount: number | null },
    Error,
    { source: DriftSource; id: string }
  >({
    mutationFn: async ({ source, id }) => {
      if (source === 'TCM') {
        const res = await tcmApi.unstageTcmDrifts(tenantId!, [id])
        return { source, revertedCount: res.revertedCount }
      }
      await api.updateDriftStatus(id, DriftStatus.New)
      return { source, revertedCount: null }
    },
    onSuccess: ({ source, revertedCount }) => {
      // TCM no-op: one row requested, zero reverted → it was already applied or
      // rejected. Tell the truth with neutral (info) styling, not a success.
      if (source === 'TCM' && revertedCount !== null && isUnstageNoop(revertedCount, 1)) {
        toast('That change had already been applied or rejected. Nothing to pull back.', {
          duration: 5000,
        })
      } else {
        toast.success('Pulled that one back. It is active again.')
      }
      invalidateAllDriftQueries()
    },
    onError: err => {
      toast.error(getServerMessage(err, 'Could not undo that staging. Try again, or check the server log.'))
    },
  })

  // ── Apply N staged changes (the manual batched write) ──────────────
  // Fans out to both backends via the pure applyAllStaged orchestrator: TCM
  // → one new monitor version; DSC → one ADO PR per tenant. Never throws —
  // per-source errors come back in the result so the toast can name exactly
  // what landed and what didn't (e.g. a TCM 409 "monitor changed" while the
  // DSC PR still opened).
  const applyMutation = useMutation({
    mutationFn: () => {
      // Capture what was staged AT FIRE TIME so onSuccess can tell "DSC wasn't
      // attempted" from "DSC was attempted but did nothing" (F4). Guarded by
      // canApply, so neither source fires while the staged queue is unreadable.
      const fireHasTcm = tcmStagedCount > 0
      const fireHasDsc = dscStagedCount > 0
      return applyAllStaged({
        tenantId: tenantId!,
        hasTcmStaged: fireHasTcm,
        hasDscQueued: fireHasDsc,
        // Wrap in arrows so the methods keep their object context regardless of
        // how the orchestrator invokes them.
        api: {
          applyStagedTcmDrifts: (tid, comment) => tcmApi.applyStagedTcmDrifts(tid, comment),
          processBatchedApprovals: tid => api.processBatchedApprovals(tid),
        },
      }).then(result => ({ result, fireHasDsc }))
    },
    onSuccess: ({ result, fireHasDsc }) => {
      // Classify per source. classifyApplyOutcome folds in the DSC lying-success
      // (processed===0 while DSC was queued) as a failure, and never emits the
      // TCM-only "re-scan and re-stage" advice for a DSC fault (F3/F4).
      const outcome = classifyApplyOutcome(result, fireHasDsc)

      // Success-copy parts only for sources that actually landed. Customer
      // words: "watched baseline" not monitor internals, no baseline hash
      // (that detail lives in the audit trail / TCM Lab for operators).
      const parts: string[] = []
      if (outcome.tcmLanded && result.tcm) {
        parts.push('Watched baseline updated')
      }
      if (outcome.dscLanded && result.dsc?.pullRequests?.length) {
        const prIds = result.dsc.pullRequests.map(pr => `#${pr.prId}`).join(', ')
        parts.push(`Pull request ${prIds} opened`)
      }

      if (outcome.failures.length > 0) {
        // One toast per failed source, each carrying the verbatim server message
        // (or its per-source fallback) — no hardcoded cross-source advice.
        // #18 S2: a fold-time failure (accepting a new policy) arrives as one of
        // four pinned ProblemDetails titles whose raw `detail` is operator-
        // voiced. Map those to customer copy and NEVER surface the raw detail;
        // any other failure keeps the existing verbatim-server-message path.
        outcome.failures.forEach(f => {
          console.error(`[drift-staging] ${f.source} apply failed`, f.error)
          const foldCopy = foldErrorCopyFromError(f.error)
          if (foldCopy) {
            toast.error(`${foldCopy.headline} ${foldCopy.body}`, { duration: 7000 })
          } else {
            toast.error(getServerMessage(f.error, f.fallback), { duration: 6000 })
          }
        })
        // If something DID land alongside a failure, acknowledge it quietly so
        // the user isn't left wondering whether the good half went through.
        if (parts.length > 0) {
          toast.success(`Partly applied. ${parts.join(' · ')}.`)
        }
      } else {
        toast.success(`Applied. ${parts.join(' · ') || 'Baseline updated.'}`)
      }
      invalidateAllDriftQueries()
    },
    onError: err => {
      // applyAllStaged itself never throws, so this only fires on a bug in the
      // mutation wrapper; still surface a useful message.
      toast.error(getServerMessage(err, 'Apply did not land. Try again, or check the server log.'))
    },
  })

  // ── Run-triage trigger (PA-only) ────────────────────────────────────
  // Ask Guardian to read the open queue and stamp advisory suggestions.
  // Writes only the Triage* columns — nothing leaves any queue — so after a
  // run we just refresh the lists and the pills appear. The tally is surfaced
  // in customer words (rows read, how many look like routine churn).
  const runTriageMutation = useMutation<DriftTriageRunTally, Error, void>({
    mutationFn: () => tcmApi.runTriage(tenantId!),
    onSuccess: tally => {
      const scanned = tally.tcmRowsScanned + tally.dscRowsScanned
      const churn = tally.bySuggestion?.ExpectedChurn ?? 0
      toast.success(triageRunSummary(scanned, churn))
      invalidateAllDriftQueries()
    },
    onError: err => {
      toast.error(
        getServerMessage(
          err,
          "Guardian couldn't read the queue just now. Try again, or check the server log.",
        ),
      )
    },
  })

  // ── Accept Guardian's suggestion (bulk) ─────────────────────────────
  // Fans the selection out per source + suggestion. Phase 1 only ever
  // surfaces ExpectedChurn, so the routing is: TCM rows → bulk-waive; DSC
  // rows → the existing bulk status change to Ignored. Each source's call is
  // caught independently so one failing doesn't strand the other, and the
  // failed source's rows stay selected for a clean retry (mirrors the
  // bulk-stage partial-failure discipline above).
  interface AcceptSuggestionOutcome {
    dscIds: string[]
    tcmIds: string[]
    dscThrew: boolean
    tcmThrew: boolean
    tcmResult: TcmBulkWaiveResult | null
    sourceErrors: { source: 'TCM' | 'DSC'; error: unknown }[]
  }

  const acceptSuggestionMutation = useMutation<
    AcceptSuggestionOutcome,
    Error,
    {
      suggestion: 'SchemaPhantom' | 'RenderingNoise' | 'ExpectedChurn'
      dscIds: string[]
      tcmIds: string[]
      comment?: string
    }
  >({
    mutationFn: async ({ suggestion, dscIds, tcmIds, comment }) => {
      const sourceErrors: { source: 'TCM' | 'DSC'; error: unknown }[] = []
      let dscThrew = false
      let tcmThrew = false
      let tcmResult: TcmBulkWaiveResult | null = null
      // Phase 1 dispositions ExpectedChurn only. SchemaPhantom / RenderingNoise
      // routing (write Classification via apply-classification) is Phase 2 — the
      // button never surfaces for them yet (the copy map is forward-compatible,
      // but we build no dead routing). Guard so a future surfaced suggestion
      // can't silently no-op.
      if (suggestion !== 'ExpectedChurn') {
        throw new Error('Only routine-churn suggestions can be accepted in bulk today.')
      }
      await Promise.all([
        tcmIds.length
          ? tcmApi
              .bulkWaiveTcmDrifts(tenantId!, tcmIds, comment)
              .then((r: TcmBulkWaiveResult) => {
                tcmResult = r
              })
              .catch((error: unknown) => {
                tcmThrew = true
                sourceErrors.push({ source: 'TCM', error })
              })
          : Promise.resolve(),
        dscIds.length
          ? api
              .bulkStatusChangeDrifts(dscIds, 'Ignored', comment)
              .catch((error: unknown) => {
                dscThrew = true
                sourceErrors.push({ source: 'DSC', error })
              })
          : Promise.resolve(),
      ])
      return { dscIds, tcmIds, dscThrew, tcmThrew, tcmResult, sourceErrors }
    },
    onSuccess: outcome => {
      const anyThrew = outcome.dscThrew || outcome.tcmThrew
      if (!anyThrew) {
        // TCM count is server-side truth (some rows may have been staged /
        // already waived); DSC has no per-row echo so we count what we sent.
        const disposed =
          (outcome.tcmResult?.waivedCount ?? outcome.tcmIds.length) + outcome.dscIds.length
        toast.success(`Guardian's suggestion accepted on ${pluralizeChange(disposed)}.`)
        setSelectedKeys(new Set())
      } else {
        outcome.sourceErrors.forEach(e => {
          console.error(`[drift-triage] ${e.source} accept-suggestion failed`, e.error)
        })
        const first = outcome.sourceErrors[0]
        toast.error(
          getServerMessage(
            first?.error,
            "Couldn't accept that suggestion for every row. The ones that didn't apply are still selected.",
          ),
          { duration: 6000 },
        )
        // Keep exactly the failed source's rows selected for a clean retry.
        const keep = new Set<string>()
        if (outcome.tcmThrew) outcome.tcmIds.forEach(id => keep.add(selectionKey('TCM', id)))
        if (outcome.dscThrew) outcome.dscIds.forEach(id => keep.add(selectionKey('DSC', id)))
        setSelectedKeys(keep)
      }
      invalidateAllDriftQueries()
    },
    onError: err => {
      toast.error(
        getServerMessage(err, "Couldn't accept that suggestion. Try again, or check the server log."),
      )
    },
  })

  // Either source erroring is surfaced as a row-level retry — we don't
  // hard-fail the whole tab because partial data is still useful. The
  // server message is preserved on the row so the user knows which
  // source faltered. `tcmQueueError` also gates the split-line's TCM lanes
  // (alongside each lane's own count-probe error) and the all-clear empty
  // state below: the two count probes can succeed even when the QUEUE fetch
  // (tcmQuery) fails, so without this a failed TCM fetch would otherwise
  // silently zero out both lanes and, if DSC is also empty, render "Nothing
  // needs your attention" over drift Guardian simply couldn't read.
  const dscError = dscQuery.error as Error | null
  const tcmQueueError = tcmQuery.isError
  const isLoading = dscQuery.isLoading || tcmQuery.isLoading

  const mergedRows = useMemo<UnifiedDriftRow[]>(() => {
    const dscRows = (dscQuery.data?.items ?? []).map(normalizeToUnifiedRow)
    const tcmRows = (tcmQuery.data?.items ?? []).map(normalizeTcmToUnifiedRow)
    // Backend already sorted each source by severity DESC — this is the
    // cross-source tiebreaker only.
    return [...dscRows, ...tcmRows].sort(compareUnifiedRows)
  }, [dscQuery.data?.items, tcmQuery.data?.items])

  // Staged rows, merged the same way the Active queue is, for the Staged
  // section's per-row pill + Undo.
  const stagedRows = useMemo<UnifiedDriftRow[]>(() => {
    const dscRows = (dscStagedQuery.data?.items ?? []).map(normalizeToUnifiedRow)
    const tcmRows = (tcmStagedQuery.data?.items ?? []).map(normalizeTcmToUnifiedRow)
    return [...dscRows, ...tcmRows].sort(compareUnifiedRows)
  }, [dscStagedQuery.data?.items, tcmStagedQuery.data?.items])

  // ── Creation-detector rows (#18 S2) ────────────────────────────────
  // A detector row is a brand-new policy Guardian spotted between checks
  // (`source === 'CreationDetector'`, `driftType === 'Added'`). The unified
  // row shape doesn't carry the detector dimension (it's normalised away), so
  // we derive a lookup from the raw TCM summary items — keyed by the same id
  // the unified row uses (TcmDriftEntity.Id) — and hand the queue row its
  // advisor-voice "New policy" copy. Active + staged items both contribute so
  // a staged new policy still reads as one. Customer name comes from the live
  // displayName the detector captured.
  const detectorRowsById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof creationDetectorCopy>>()
    const consider = [
      ...(tcmQuery.data?.items ?? []),
      ...(tcmStagedQuery.data?.items ?? []),
    ]
    for (const d of consider) {
      if (
        isCreationDetectorRow({
          source: 'TCM',
          pipelineSource: d.source,
          driftType: d.driftType,
        })
      ) {
        const name = d.resourceDisplayName?.trim() || null
        map.set(d.id, creationDetectorCopy(name))
      }
    }
    return map
  }, [tcmQuery.data?.items, tcmStagedQuery.data?.items])

  // ── Removed-policy rows (retire-OUT) ───────────────────────────────
  // The symmetric half of the creation detector: a watched policy that has
  // vanished from the tenant surfaces as a removal row (`source ∈
  // {DeletionDetector, SnapshotDiff}`, `driftType === 'Removed'`). Same derive
  // pattern as `detectorRowsById` — keyed on the unified row id — so the queue
  // can show "Removed policy" framing + the retire verdict. Gated on the SOURCE
  // (via isDeletionDetectorRow), never on `driftType` alone, so an ordinary
  // `Monitor` `Removed` row keeps its generic framing (it is not retire-
  // eligible on the server). Active + staged items both contribute.
  const removedPolicyById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof deletionDetectorCopy>>()
    const consider = [
      ...(tcmQuery.data?.items ?? []),
      ...(tcmStagedQuery.data?.items ?? []),
    ]
    for (const d of consider) {
      if (
        isDeletionDetectorRow({
          source: 'TCM',
          pipelineSource: d.source,
          driftType: d.driftType,
        })
      ) {
        const name = d.resourceDisplayName?.trim() || null
        map.set(d.id, deletionDetectorCopy(name))
      }
    }
    return map
  }, [tcmQuery.data?.items, tcmStagedQuery.data?.items])

  // ── Acknowledgeable (full-capture sweep) rows ───────────────────────
  // Tier-2 snapshot-diff `Added`/`Modified` rows go through the same
  // stage → apply flow as any other row, flipping to Accepted with no
  // baseline write (#95). Same keyed-by-id derive as `detectorRowsById` so
  // the queue can say why on the row and note the acknowledge-only outcome
  // in the bulk-accept toast. Gated on the SOURCE via isAcknowledgeRow;
  // `Removed` rows are handled separately (they retire).
  const acknowledgeIds = useMemo(() => {
    const ids = new Set<string>()
    for (const d of tcmQuery.data?.items ?? []) {
      if (
        isAcknowledgeRow({
          source: 'TCM',
          pipelineSource: d.source,
          driftType: d.driftType,
        })
      ) {
        ids.add(d.id)
      }
    }
    return ids
  }, [tcmQuery.data?.items])
  const isAcknowledgeUnifiedRow = (r: UnifiedDriftRow) =>
    r.source === 'TCM' && acknowledgeIds.has(r.id)

  // ── Upgrade-introduced (new-setting) rows ──────────────────────────
  // A DSC SharePoint/OneDrive row whose baseline never carried this property
  // (oldValue normalises to "Not Present"). These aren't a change the customer
  // made — a newer SharePoint/OneDrive check began reporting the setting — so
  // we tag them "New setting" and let the customer bulk-accept to fold them
  // into the baseline. Keyed by the DSC drift id like the detector maps above.
  // Derived from the RAW DSC items (oldValue/component live there, not on the
  // unified row). Active + staged both contribute so a staged new setting still
  // reads as one. DSC rows on a Hybrid tenant are SPO/ODB-only by construction;
  // the predicate's component check keeps the tag honest regardless.
  const upgradeRowsById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof upgradeIntroducedCopy>>()
    const consider = [
      ...(dscQuery.data?.items ?? []),
      ...(dscStagedQuery.data?.items ?? []),
    ]
    for (const d of consider) {
      if (isUpgradeIntroducedRow({ source: 'DSC', component: d.component, oldValue: d.oldValue })) {
        map.set(d.id, upgradeIntroducedCopy())
      }
    }
    return map
  }, [dscQuery.data?.items, dscStagedQuery.data?.items])

  // Per-severity counts over the loaded rows. They are the queue's counts only
  // when the whole queue is loaded (the API gives no per-severity totals for
  // the open queue), so the Severity select shows them only then.
  const loadedSeverityCounts = useMemo<SeverityCounts>(() => {
    const counts: SeverityCounts = { critical: 0, high: 0, medium: 0, low: 0 }
    for (const row of mergedRows) {
      if (row.severity && row.severity !== 'Info') counts[row.severity.toLowerCase() as keyof SeverityCounts] += 1
    }
    return counts
  }, [mergedRows])

  // The workloads present in the loaded queue, for the Workload select.
  const workloadOptions = useMemo(() => {
    const set = new Set<string>()
    for (const row of mergedRows) set.add(workloadLabelForResourceType(row.resourceType))
    return Array.from(set).sort()
  }, [mergedRows])

  // Severity, workload and search narrow a row set the same way on every tab.
  const matchesFilters = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (row: UnifiedDriftRow) => {
      if (severityFilter !== 'All' && row.severity !== severityFilter) return false
      if (workloadFilter && workloadLabelForResourceType(row.resourceType) !== workloadFilter) return false
      if (!q) return true
      // The raw keys and the names the rows show, so either finds the row.
      const haystack = [
        prettifyResourceType(row.resourceType),
        driftResourceName(row.resourceType),
        row.resourceName ?? '',
        row.propertyPath ?? '',
        rowTitle(row),
        workloadLabelForResourceType(row.resourceType),
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    }
  }, [search, severityFilter, workloadFilter])

  // The open queue, filtered. Select-all, the batch bar and the group
  // checkboxes all work over exactly these rows, so they can never act on a
  // row the user can't see (every filter change also clears the selection).
  const visibleRows = useMemo(() => mergedRows.filter(matchesFilters), [mergedRows, matchesFilters])
  const visibleStagedRows = useMemo(() => stagedRows.filter(matchesFilters), [stagedRows, matchesFilters])

  // Back to baseline: both sources' remediated rows, merged like the queue.
  // DSC rows are the SPO/ODB-scoped set (cutover-superseded rows excluded).
  const backRows = useMemo<UnifiedDriftRow[]>(() => {
    if (!backListOpen) return []
    const dscRows = dscBackItems.map(normalizeToUnifiedRow)
    const tcmRows = (tcmBackQuery.data?.items ?? []).map(normalizeTcmToUnifiedRow)
    return [...dscRows, ...tcmRows].sort(compareUnifiedRows).filter(matchesFilters)
  }, [backListOpen, dscBackItems, tcmBackQuery.data?.items, matchesFilters])

  // For the source line and the tab counts. Counts are real — no fabrication.
  // The two continuous-watch tiers (Continuous watch = Tier-1 monitor, Full
  // capture = Tier-2 recurring sweep; Adam, 2026-09-23,
  // terminology-decisions-2026-09-22.md) read their own server-side
  // `totalCount` from tcmContinuousCountQuery / tcmFullCaptureCountQuery above
  // — a true per-Source total, not a count over the one loaded queue page (a
  // busy tenant's Tier-2 sweep can run well past the queue's
  // top-50-by-severity page). The SharePoint & OneDrive count matches: the
  // server's `total`, falling back to the loaded-page length only if `total`
  // is ever absent.
  const dscCount = dscQuery.data?.total ?? dscQuery.data?.items.length ?? 0
  const tcmContinuousCount = tcmContinuousCountQuery.data?.totalCount ?? 0
  const tcmFullCaptureCount = tcmFullCaptureCountQuery.data?.totalCount ?? 0

  // Both continuous-watch tiers go "couldn't load" when either the QUEUE
  // fetch (tcmQuery) failed OR their own count probe failed — the probes can
  // succeed on an empty tenant while the queue itself errors, so gating on the
  // probe alone would show a confident "0" over a fetch that actually failed
  // (see tcmQueueError above). Each renders '—' while its probe is still
  // pending (no cached total yet) so first paint / the includeFiltered toggle
  // never flashes a false "0" before keepPreviousData catches up.
  const tcmContinuousUnavailable = tcmQueueError || tcmContinuousCountQuery.isError
  const tcmFullCaptureUnavailable = tcmQueueError || tcmFullCaptureCountQuery.isError

  // Tab counts: server totals, hidden (null) while unknown — never a fabricated 0.
  const openCount =
    dscError || tcmQueueError || dscQuery.isPending || tcmQuery.isPending
      ? null
      : (dscQuery.data?.total ?? 0) + (tcmQuery.data?.totalCount ?? 0)
  // Each source loads only its PAGE_SIZE most severe rows, so the loaded rows
  // are the open queue only when they reach the server total. The Severity
  // select's All and the footer read the server total; the per-severity
  // options count the loaded rows only when those are the whole queue.
  const wholeQueueLoaded = openCount !== null && mergedRows.length >= openCount
  const severityOptions = severityFilterOptions(
    openCount,
    wholeQueueLoaded ? loadedSeverityCounts : undefined,
    countsUnknown,
  )
  const openFooter =
    openCount === null
      ? `${visibleRows.length} shown · ranked by severity`
      : wholeQueueLoaded || visibleRows.length === mergedRows.length
        ? `${visibleRows.length} of ${openCount} shown · ranked by severity`
        : // The filters narrow only the loaded rows: say so, beside the real total.
          `${visibleRows.length} of the ${mergedRows.length} loaded match · ${openCount} open · ranked by severity`
  const acceptedCount = stagedQueueUnavailable || dscStagedQuery.isPending || tcmStagedQuery.isPending ? null : stagedCount
  // DSC contributes its loaded SPO/ODB rows, not the server `total` (which
  // also counts the cutover-superseded rows the list hides). The endpoint has
  // no component filter, so those rows come from one page filtered here: when
  // the server holds more remediated rows than that page, SPO/ODB rows past it
  // are missing and the count is unknown (null), never a silent lower bound.
  const dscBackPartial = (dscBackQuery.data?.total ?? 0) > (dscBackQuery.data?.items.length ?? 0)
  const backCount =
    dscBackQuery.isError || tcmBackQuery.isError || dscBackQuery.isPending || tcmBackQuery.isPending || dscBackPartial
      ? null
      : dscBackItems.length + (tcmBackQuery.data?.totalCount ?? 0)

  const selectedCount = selectedKeys.size
  const bulkPending = bulkAcceptMutation.isPending
  const applyPending = applyMutation.isPending
  const acceptSuggestionPending = acceptSuggestionMutation.isPending

  // ── Selected rows + shared-suggestion consensus ─────────────────────
  // The bulk "Accept Guardian's suggestion" action only appears when EVERY
  // selected (active) row shares one surfaceable suggestion — so a bulk
  // accept can never fan a disposition across rows Guardian read differently
  // (blueprint §0.5-F3). Rows Guardian left as RealChange (no pill) carry no
  // suggestion, so any RealChange in the selection collapses the consensus to
  // null and the button stays hidden.
  const selectedTriageRows = useMemo(() => {
    const rows: UnifiedDriftRow[] = []
    for (const row of mergedRows) {
      if (selectedKeys.has(selectionKey(row.source, row.id))) rows.push(row)
    }
    return rows
  }, [mergedRows, selectedKeys])
  const selectedSuggestion = useMemo(
    () => sharedTriageSuggestion(selectedTriageRows.map(r => r.raw.triageSuggestion)),
    [selectedTriageRows],
  )
  const selectedSuggestionCopy = selectedSuggestion
    ? triageSuggestionCopy({ triageSuggestion: selectedSuggestion })
    : null
  // The shared reason Guardian gave — prefilled into the evidence modal's
  // comment field (the human owns the final wording). First row's reason is
  // representative: a shared suggestion is authored from one shared rule/read.
  const selectedSharedReason = selectedTriageRows[0]?.raw.triageReason ?? ''

  // "Accept as new baseline" opens the comment modal; the actual stage fires
  // from executeBulkAccept once the user confirms (with or without a note).
  function handleBulkAccept() {
    if (selectedCount === 0) return
    setShowBulkCommentModal(true)
  }

  function executeBulkAccept(comment: string) {
    const dscIds: string[] = []
    const tcmIds: string[] = []
    selectedKeys.forEach(key => {
      const [source, ...rest] = key.split(':')
      const id = rest.join(':')
      if (source === 'DSC') dscIds.push(id)
      else if (source === 'TCM') tcmIds.push(id)
    })
    setShowBulkCommentModal(false)
    if (dscIds.length === 0 && tcmIds.length === 0) return
    // Empty string from the modal means "no note" — normalize to undefined so
    // the API omits `comment` rather than persisting an empty audit string.
    bulkAcceptMutation.mutate({ dscIds, tcmIds, comment: comment || undefined })
  }

  function handleApply() {
    // Never apply while the staged queue is unreadable — a partial apply could
    // strand the unreadable source's rows (F1/F8). canApply folds that gate in.
    if (!canApply || applyPending) return
    applyMutation.mutate()
  }

  // "Accept Guardian's suggestion (N)" opens the evidence modal; the actual
  // disposition fires from executeAcceptSuggestion once the human confirms the
  // (editable, prefilled) reason.
  function handleAcceptSuggestion() {
    if (!selectedSuggestion || selectedCount === 0) return
    setShowAcceptSuggestionModal(true)
  }

  function executeAcceptSuggestion(comment: string) {
    if (!selectedSuggestion) return
    const dscIds: string[] = []
    const tcmIds: string[] = []
    for (const row of selectedTriageRows) {
      if (row.source === 'DSC') dscIds.push(row.id)
      else tcmIds.push(row.id)
    }
    setShowAcceptSuggestionModal(false)
    if (dscIds.length === 0 && tcmIds.length === 0) return
    acceptSuggestionMutation.mutate({
      suggestion: selectedSuggestion,
      dscIds,
      tcmIds,
      comment: comment || undefined,
    })
  }

  function handleDrawerClose() {
    if (isControlled) {
      onSelectDrift?.(null)
    }
    setInternalSelected(null)
  }

  // Drawer source resolution. The drawer opens for a controlled selection
  // from outside the table (the page's top items). Find the matching row in
  // the merged set to recover its source; fall back to the parent's hint,
  // then DSC, for backwards compatibility.
  const selectedRow = isControlled
    ? mergedRows.find(r => r.id === controlledSelectedDriftId) ?? null
    : internalSelected
      ? mergedRows.find(r => r.id === internalSelected.id) ?? null
      : null
  const drawerOpen = isControlled
    ? !!controlledSelectedDriftId
    : !!internalSelected
  const drawerId = isControlled
    ? controlledSelectedDriftId ?? null
    : internalSelected?.id ?? null
  // Wave 7 codex fix — prefer the explicit parent-passed source hint over the
  // 'DSC' fallback. The previous fallback chain defaulted to 'DSC' when a
  // controlled selection's row hadn't loaded into mergedRows yet (e.g., a
  // Top Items click feeding a TCM id from Slice C's UNION), making the
  // drawer hit the wrong audit-history endpoint.
  const drawerSource: DriftSource =
    selectedRow?.source
    ?? internalSelected?.source
    ?? (selectedDriftSource === 'TCM' || selectedDriftSource === 'DSC' ? selectedDriftSource : 'DSC')

  // Write handlers — branched per source. TCM Accept stages via
  // approveTcmDrift; DSC Accept stages via approveDrift. Both reject through
  // their source's reject route. The id is the DSC drift id or the TcmDrift
  // PK depending on source. TCM actions require tenantId (its routes are
  // tenant-scoped); we guard on it.
  const approveFor = (source: DriftSource) => (id: string, comment?: string) => {
    if (source === 'TCM') {
      if (!tenantId) return
      tcmApproveMutation.mutate({ driftId: id, comment })
    } else {
      dscApproveMutation.mutate({ driftId: id, comment })
    }
  }
  const rejectFor = (source: DriftSource) => (id: string, comment?: string) => {
    if (source === 'TCM') {
      if (!tenantId) return
      tcmRejectMutation.mutate({ driftId: id, comment })
    } else {
      dscRejectMutation.mutate({ driftId: id, comment })
    }
  }
  const writeInFlightFor = (source: DriftSource) =>
    source === 'TCM'
      ? tcmApproveMutation.isPending || tcmRejectMutation.isPending
      : dscApproveMutation.isPending || dscRejectMutation.isPending

  function handleRefresh() {
    invalidateAllDriftQueries()
  }

  function openMonitoringTab() {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', 'monitoring')
      return next
    })
  }

  const hasTenant = !!tenantId
  const tenantName = userTenants.find(t => t.tenantId === tenantId)?.name
  // Before the first assessment there is nothing to triage or export, so the
  // queue offers neither (a loaded queue keeps both even if the stats failed).
  const queueUnknownAndEmpty = countsUnknown && mergedRows.length === 0

  // ── Table: columns, groups, batch bar ──────────────────────────────
  const rowCopy = (row: UnifiedDriftRow) => ({
    newPolicy: detectorRowsById.get(row.id) ?? null,
    removedPolicy: removedPolicyById.get(row.id) ?? null,
    upgraded: upgradeRowsById.get(row.id) ?? null,
    acknowledge: isAcknowledgeUnifiedRow(row) ? acknowledgeCopy() : null,
  })

  const severityColumn: DataColumn<UnifiedDriftRow> = {
    id: 'severity',
    header: 'Severity',
    nowrap: true,
    cell: row => {
      // Severity provenance ("why is this High?") is continuous-watch-only for
      // now — DSC rows don't carry the WO-5 signal columns. Null on any signal
      // gap (pre-WO-5 row, not yet enriched) degrades to no tooltip rather
      // than a partial/misleading one.
      const provenance =
        row.source === 'TCM'
          ? composeSeverityProvenance({
              effectiveSeverity: row.severity,
              staticSeverity: (row.raw as TcmDriftSummary).staticSeverity,
              opaSeverity: (row.raw as TcmDriftSummary).opaSeverity,
              aiRiskScore: (row.raw as TcmDriftSummary).aiRiskScore,
            })
          : null
      return <SeverityCell severity={row.severity} provenance={provenance} />
    },
  }

  const settingColumn: DataColumn<UnifiedDriftRow> = {
    id: 'setting',
    header: 'Setting',
    primary: true,
    width: '34%',
    cell: row => (
      <SettingCell
        row={row}
        {...rowCopy(row)}
        onToggle={() => setExpandedKeys(keys => toggleId(keys, selectionKey(row.source, row.id)))}
      />
    ),
  }

  // The status rides in the When cell as a tag (mockup 02 has no Status
  // column), only when it differs from the tab's own state: a staged DSC row
  // (Awaiting remediation) in the open queue.
  const tabState = TAB_STATE[statusTab]
  const whenColumn: DataColumn<UnifiedDriftRow> = {
    id: 'when',
    header: 'When',
    nowrap: true,
    cell: row => (
      <WhenCell
        at={row.detectedAt}
        status={row.displayState === tabState ? null : displayStateLabel(row.displayState)}
        statusTitle={`Pipeline status: ${row.status || (row.source === 'TCM' ? 'Active' : '—')}`}
      />
    ),
  }

  const changeColumn: DataColumn<UnifiedDriftRow> = {
    id: 'change',
    header: 'Change',
    nowrap: true,
    hideBelow: 'c-md',
    cell: row => <ChangeSummary row={row} {...rowCopy(row)} />,
  }

  const undoColumn: DataColumn<UnifiedDriftRow> = {
    id: 'undo',
    header: <span className="sr-only">Undo</span>,
    nowrap: true,
    align: 'end',
    cell: row => {
      const pending =
        undoStageMutation.isPending &&
        undoStageMutation.variables?.source === row.source &&
        undoStageMutation.variables?.id === row.id
      return (
        <Button
          kind="ghost"
          size="sm"
          onClick={() => undoStageMutation.mutate({ source: row.source, id: row.id })}
          disabled={pending || undoStageMutation.isPending}
        >
          {pending ? 'Undoing' : 'Undo'}
        </Button>
      )
    },
  }

  const columns: DataColumn<UnifiedDriftRow>[] =
    statusTab === 'accepted'
      ? [severityColumn, settingColumn, whenColumn, ...(canWrite ? [undoColumn] : [])]
      : [severityColumn, settingColumn, changeColumn, whenColumn]

  const tableRows = statusTab === 'accepted' ? visibleStagedRows : statusTab === 'back' ? backRows : visibleRows

  // A resource with several drifted settings rolls up under one collapsed
  // group header; a resource with one drifted setting stays a plain row
  // (flatSingles), so the queue reads one row per resource either way.
  // Ordered rules that only moved because one rule was added or moved above
  // them fold into one priority-shift group instead (drift-priority-shift.ts).
  const priorityGroupById = priorityShiftGroups(tableRows)
  // The note only says to accept where the reader can: the open queue, with write access.
  const priorityCopyOpts = { actionable: canWrite && statusTab === 'open' }
  const groups: DataTableGroups<UnifiedDriftRow> = {
    key: row => priorityGroupById.get(row.id) ?? resourceKey(row),
    label: (_k, rows) =>
      priorityGroupById.has(rows[0].id)
        ? priorityShiftCopy(rows, priorityCopyOpts).label
        : rows[0].resourceName ?? driftResourceName(rows[0].resourceType),
    flatSingles: true,
    expandAll: true,
    header: (_k, rows) =>
      priorityGroupById.has(rows[0].id) ? (
        <PriorityShiftHeader rows={rows} copy={priorityShiftCopy(rows, priorityCopyOpts)} />
      ) : (
        <GroupHeader rows={rows} upgradeLabel={groupUpgradeLabel(rows)} />
      ),
  }

  // If every setting in a resource is a DSC-upgrade "New setting" phantom
  // (1.26 schema noise, not an admin change), name that on the group header so
  // it isn't hidden behind the rollup. Mixed groups stay unlabelled — tagging
  // the whole resource as noise when only some rows are would mislead.
  function groupUpgradeLabel(rows: UnifiedDriftRow[]): string | null {
    return rows.every(r => upgradeRowsById.has(r.id)) ? upgradeRowsById.get(rows[0].id)?.kicker ?? null : null
  }

  const selectable = canWrite && statusTab === 'open'
  const batchActions: BatchAction[] = [
    {
      id: 'accept',
      label: 'Accept as new baseline',
      shortLabel: 'Accept',
      icon: <IconCheck />,
      onClick: handleBulkAccept,
      disabled: bulkPending || acceptSuggestionPending,
    },
    // Accept Guardian's suggestion — only when every selected row shares one
    // surfaceable suggestion. Opens the evidence modal, not a blind bulk write.
    ...(selectedSuggestionCopy
      ? [
          {
            id: 'suggestion',
            label: `Accept Guardian's suggestion (${selectedCount})`,
            shortLabel: 'Suggestion',
            onClick: handleAcceptSuggestion,
            disabled: bulkPending || acceptSuggestionPending,
          },
        ]
      : []),
  ]

  const tableEmpty = ((): ReactNode => {
    if (statusTab === 'open') {
      // A source erroring must never read as "all clear" — mergedRows drops a
      // failed source's rows to [], so an empty DSC queue plus a failed TCM
      // fetch would otherwise show the all-clear over drift Guardian couldn't read.
      if ((dscError || tcmQueueError) && mergedRows.length === 0) {
        return "Guardian couldn't load this queue. Refresh to try again."
      }
      const filterMiss = 'Nothing in the loaded queue matches these filters. Clear them to see everything.'
      // Same words as the DSC table (drift-carbon-helpers.ts), and only for a
      // never-scanned tenant's unfiltered view: after a failed stats call the
      // queue still loaded, so its emptiness is real.
      if (unscanned && mergedRows.length === 0) {
        const filtered = search.trim() !== '' || severityFilter !== 'All' || workloadFilter !== ''
        if (filtered) return filterMiss
        return (
          <>
            <span className="block text-text-primary">{QUEUE_NOT_ASSESSED.title}</span>
            <span className="mt-1 block text-helper">{QUEUE_NOT_ASSESSED.body}</span>
          </>
        )
      }
      if (mergedRows.length === 0) return 'Nothing to review.'
      return filterMiss
    }
    if (statusTab === 'accepted') {
      if (stagedQueueUnavailable) return "Guardian couldn't read the changes waiting to apply."
      return stagedRows.length === 0 ? 'Nothing is waiting to apply.' : 'Nothing waiting to apply matches these filters.'
    }
    if (dscBackQuery.isError || tcmBackQuery.isError) return "Guardian couldn't load the drift that is back to baseline."
    if (dscBackPartial) {
      return 'Nothing in the loaded page is back to baseline. Guardian read only part of the SharePoint & OneDrive history.'
    }
    return 'Nothing is back to baseline yet.'
  })()

  const tableLoading =
    statusTab === 'open'
      ? isLoading
      : statusTab === 'accepted'
        ? dscStagedQuery.isLoading || tcmStagedQuery.isLoading
        : backListOpen && (dscBackQuery.isFetching || tcmBackQuery.isFetching) && backRows.length === 0

  const lanes = (
    <p className="m-0 flex flex-wrap items-center gap-x-4 gap-y-1 text-helper text-text-helper">
      <span>
        Continuous watch{' '}
        <b className="font-semibold tabular-nums text-text-primary">
          {tcmContinuousUnavailable || tcmContinuousCountQuery.isPending ? '—' : tcmContinuousCount}
        </b>
        {tcmContinuousUnavailable ? <span> (couldn&apos;t load)</span> : null}
      </span>
      <span>
        Full capture{' '}
        <b className="font-semibold tabular-nums text-text-primary">
          {tcmFullCaptureUnavailable || tcmFullCaptureCountQuery.isPending ? '—' : tcmFullCaptureCount}
        </b>
        {tcmFullCaptureUnavailable ? <span> (couldn&apos;t load)</span> : null}
      </span>
      <span>
        SharePoint &amp; OneDrive{' '}
        <b className="font-semibold tabular-nums text-text-primary">{dscError ? '—' : dscCount}</b>
        {dscError ? <span> (couldn&apos;t load)</span> : null}
      </span>
    </p>
  )

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Action row — where the queue comes from (real per-source counts; the
          tiers carry customer words: "Continuous watch" is the Tier-1 6h
          monitor, "Full capture" the Tier-2 recurring config-sweep,
          "SharePoint & OneDrive" the scan; Adam, 2026-09-23), then Refresh,
          the PA-only triage trigger, and Apply. TCM ingestion runs on its own
          schedule, so there's no manual scan here. */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {countsUnknown ? <span aria-hidden="true" /> : lanes}
        <div className="flex flex-wrap items-center gap-2">
          <IconButton
            label="Refresh the queue"
            icon={<ArrowPathIcon strokeLinecap="square" strokeLinejoin="miter" />}
            onClick={handleRefresh}
            disabled={!hasTenant}
            size={40}
          />
          {/* Drift Triage Copilot — PA-only trigger. Guardian reads the open
              queue and stamps advisory suggestions; nothing is disposed until a
              human accepts. Hidden for non-PA users. */}
          {isPlatformAdmin && !queueUnknownAndEmpty ? (
            <Button
              kind="tertiary"
              size="md"
              onClick={() => runTriageMutation.mutate()}
              disabled={!hasTenant}
              loading={runTriageMutation.isPending}
            >
              {runTriageMutation.isPending ? 'Reading the queue' : 'Ask Guardian to triage this queue'}
            </Button>
          ) : null}
          {canApply && canWrite ? (
            <Button kind="primary" size="md" onClick={handleApply} disabled={!hasTenant} loading={applyPending}>
              {applyPending ? 'Applying' : 'Apply staged changes'}
              <span className="ml-2 tabular-nums">{stagedCount}</span>
            </Button>
          ) : null}
        </div>
      </div>

      {/* Mail-coverage-provisioning notice (RS-03). Shown while this tenant is
          in the transient ExoProvisioning state: Graph coverage is live but
          Exchange RBAC is still finishing, so mail drift isn't in this queue
          yet. Framed as in-progress and REQUIRED (still finishing setup), never
          as an optional add-on — sequenced-require retired the D2 defer model. */}
      {mailCoverageIncomplete ? (
        <InlineNotification kind="info" title="Mail coverage is still finishing setup.">
          Guardian is watching everything else for this tenant now. Mail coverage needs one more run by your
          Microsoft 365 admin to finish. Then mailbox and mail-flow changes appear here too.
        </InlineNotification>
      ) : null}

      {/* Staged-queue-unavailable notice — F1/F8. When either staged query
          errored we CANNOT read the staged queue, so we refuse to render a
          (lying) zero and instead name the gap explicitly. Staged changes may
          well exist; Apply + Undo stay off until the queue is readable. */}
      {stagedQueueUnavailable ? (
        <InlineNotification
          kind="error"
          title="The staged queue is unavailable."
          actions={
            <Button kind="tertiary" size="sm" onClick={handleRefresh} disabled={!hasTenant}>
              Retry
            </Button>
          }
        >
          Staged changes may exist. We could not read the queue, so Apply is held back to avoid a partial run. Try
          again in a moment.
        </InlineNotification>
      ) : null}

      {/* Staged notice — the count, with a plain-language reminder that
          staging is reversible and nothing has touched the live tenant yet.
          Hidden at staged count 0, while the staged queue is unreadable (the
          notice above covers that case), and for Viewers, who can't apply. */}
      {canApply && canWrite ? (
        <InlineNotification kind="info" title={stagedHeadline(stagedCount)}>
          {stagedBannerBody({ tcmStagedCount, dscStagedCount, applyPending })}
        </InlineNotification>
      ) : null}

      {/* The unified queue. Rows from both sources share one table; a row
          expands in place into the drift detail, which branches on `source`
          for the audit-history endpoint and the source-specific fields. */}
      <div id="drift-queue" className="scroll-mt-24">
      <DataTable
        title="Drifts"
        description="Settings that no longer match your baseline. Accept stages a change, and Apply lands the batch."
        columns={columns}
        rows={tableRows}
        rowId={row => selectionKey(row.source, row.id)}
        rowLabel={row => `drift ${rowTitle(row)}`}
        tabs={
          <ContainedTabs
            aria-label="Drift status"
            value={statusTab}
            onChange={applyStatusTab}
            tabs={[
              { id: 'open', label: 'Open', count: countsUnknown ? null : openCount, disabled: !hasTenant },
              { id: 'accepted', label: 'Waiting to apply', count: countsUnknown ? null : acceptedCount, disabled: !hasTenant },
              { id: 'back', label: 'Back to baseline', count: countsUnknown ? null : backCount, disabled: !hasTenant },
            ]}
          />
        }
        toolbar={
          <>
            <TableSearch value={search} onChange={applySearch} label="Search settings" />
            <TableSelect
              label="Workload"
              value={workloadFilter}
              onChange={applyWorkloadFilter}
              options={[{ value: '', label: 'All' }, ...workloadOptions.map(w => ({ value: w, label: w }))]}
            />
            <TableSelect
              label="Severity"
              value={severityFilter === 'All' ? '' : severityFilter}
              onChange={value => applySeverityFilter((value || 'All') as SeverityFilter)}
              options={severityOptions}
            />
            {queueUnknownAndEmpty ? null : (
              <IconButton
                label="Export audit"
                icon={<IconDownload />}
                onClick={() => setShowAuditExportModal(true)}
                disabled={!hasTenant}
              />
            )}
          </>
        }
        selectable={selectable}
        selectedIds={Array.from(selectedKeys)}
        onSelectionChange={ids => setSelectedKeys(new Set(ids))}
        batchActions={selectable ? batchActions : undefined}
        groups={groups}
        expandable={{
          expandedIds: expandedKeys,
          onExpandedChange: setExpandedKeys,
          render: row => (
            <DriftDetailContent
              driftId={row.id}
              source={row.source}
              tenantId={row.source === 'TCM' ? tenantId : undefined}
              onApprove={approveFor(row.source)}
              onReject={rejectFor(row.source)}
              isUpdating={writeInFlightFor(row.source)}
              readOnly={!canWrite}
              layout="row"
            />
          ),
        }}
        isLoading={tableLoading}
        empty={tableEmpty}
        footer={
          <>
            <span className="tabular-nums">
              {unscanned && tableRows.length === 0
                ? 'No drift data yet'
                : statusTab === 'open'
                ? openFooter
                : `${tableRows.length} shown · ranked by severity`}
            </span>
            <span className="flex flex-wrap items-center gap-x-2">
              {/* WO-3: schema-noise toggle. Schema phantoms (new-in-1.26
                  properties, not admin changes) are filtered out of the queue
                  by default; this names how many and lets the user reveal them. */}
              {statusTab === 'open' && noiseCount > 0 ? (
                <Button kind="ghost" size="sm" onClick={() => setIncludeFiltered(v => !v)} aria-pressed={includeFiltered}>
                  {includeFiltered
                    ? `Hide the ${noiseCount} schema-noise ${noiseCount === 1 ? 'item' : 'items'}`
                    : `Show ${noiseCount} schema-noise ${noiseCount === 1 ? 'item' : 'items'}`}
                </Button>
              ) : null}
              {/* Lifecycle pointer — the Monitoring tab owns the full story. */}
              <Button kind="ghost" size="sm" onClick={openMonitoringTab}>
                How monitoring works
              </Button>
            </span>
          </>
        }
      />
      </div>

      {/* Drift detail drawer — for a drift opened from outside the table (a
          controlled selectedDriftId). The `source` prop tells it which audit-history
          endpoint to call and which source-specific fields to render. */}
      <DriftDetailDrawer
        driftId={drawerId}
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        onApprove={approveFor(drawerSource)}
        onReject={rejectFor(drawerSource)}
        isUpdating={writeInFlightFor(drawerSource)}
        source={drawerSource}
        tenantId={drawerSource === 'TCM' ? tenantId : undefined}
        readOnly={!canWrite}
      />

      {/* Audit export modal — DSC audit table is the canonical home of
          the export today; the modal already projects TCM rows when
          R-NEW-8 from Wave 5 landed. */}
      <DriftAuditExportModal
        isOpen={showAuditExportModal}
        onClose={() => setShowAuditExportModal(false)}
        tenantId={tenantId ?? ''}
        tenantName={tenantName}
      />

      {/* Bulk-accept comment modal — one shared, optional audit note for the
          whole batch (e.g. "Adopting SPO settings introduced by the DSC
          upgrade"). Comment stays optional. */}
      <BulkActionCommentModal
        isOpen={showBulkCommentModal}
        onClose={() => setShowBulkCommentModal(false)}
        onConfirm={executeBulkAccept}
        actionStatus={'Approved' as DriftStatus}
        selectedCount={selectedCount}
        isProcessing={bulkPending}
      />

      {/* Accept-suggestion evidence modal — the human sees the count, the
          affected settings, sample rows, and the editable prefilled reason
          before Guardian's suggestion is applied to the batch (D-7). */}
      {selectedSuggestionCopy && (
        <AcceptSuggestionModal
          isOpen={showAcceptSuggestionModal}
          onClose={() => setShowAcceptSuggestionModal(false)}
          onConfirm={executeAcceptSuggestion}
          rows={selectedTriageRows}
          suggestionCopy={selectedSuggestionCopy}
          prefilledReason={selectedSharedReason}
          isProcessing={acceptSuggestionPending}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Cells                                                                      */
/* -------------------------------------------------------------------------- */

interface RowCopy {
  /** Advisor-voice copy when this row is a newly detected policy (#18 S2);
   *  null for ordinary drift. Drives the "New policy" tag so the customer can
   *  tell a brand-new policy from a changed setting at a glance. */
  newPolicy: ReturnType<typeof creationDetectorCopy> | null
  /** Advisor-voice copy when this row is a retire-eligible removal (a watched
   *  policy that vanished from the tenant); null otherwise. Mutually exclusive
   *  with `newPolicy` (a row is Added or Removed, not both). */
  removedPolicy: ReturnType<typeof deletionDetectorCopy> | null
  /** Advisor-voice copy when this DSC SharePoint/OneDrive row is a setting a
   *  newer check began reporting that the baseline never had ("New setting");
   *  null otherwise. */
  upgraded: ReturnType<typeof upgradeIntroducedCopy> | null
  /** Customer-safe copy when this continuous-watch row is a full-capture sweep
   *  finding (#95): accepting acknowledges the change without writing a new
   *  baseline. Null for every other row. */
  acknowledge: ReturnType<typeof acknowledgeCopy> | null
}

/** The row's title: the setting for a scan row, the resource for a watched one. */
function rowTitle(row: UnifiedDriftRow): string {
  // A scan row's setting reads as words (drift-names.ts, as on the DSC table
  // and in the brief); the raw key stays in the expanded detail.
  if (row.source === 'DSC') return row.propertyPath ? settingLabel(row.propertyPath) : driftResourceName(row.resourceType)
  return row.resourceName?.trim() || prettifyResourceType(row.resourceType)
}

/** The row's sub-line: workload, then where the setting lives. */
function rowSub(row: UnifiedDriftRow): string {
  const workload = workloadLabelForResourceType(row.resourceType)
  const type = prettifyResourceType(row.resourceType)
  if (row.source === 'DSC') {
    const name = row.resourceName && row.resourceName !== row.resourceType ? ` › ${row.resourceName}` : ''
    return `${workload} · ${dscResourceLabel(row.resourceType)}${name}`
  }
  const prop = row.propertyPath ? ` › ${row.propertyPath}` : ''
  return `${workload} · ${type}${prop}`
}

function SettingCell({
  row,
  newPolicy,
  removedPolicy,
  upgraded,
  acknowledge,
  onToggle,
}: { row: UnifiedDriftRow; onToggle: () => void } & RowCopy) {
  // A row is a new policy, a removed policy or a new setting, never two.
  const kicker = newPolicy?.kicker ?? removedPolicy?.kicker ?? upgraded?.kicker ?? null
  return (
    <span className="block min-w-0 [overflow-wrap:anywhere]">
      <RowToggleTitle onToggle={onToggle}>
        <TableItem title={rowTitle(row)} sub={rowSub(row)} />
      </RowToggleTitle>
      {kicker || triageSuggestionCopy(row.raw) ? (
        <span className="mt-1 flex flex-wrap items-center gap-2">
          {kicker ? <Tag kind="outline">{kicker}</Tag> : null}
          <TriagePill fields={row.raw} />
        </span>
      ) : null}
      {/* Full-capture sweep finding: say on the row that accepting it
          acknowledges the change rather than folding it into the baseline (#95). */}
      {acknowledge ? <span className="mt-1 block text-helper text-text-helper">{acknowledge.note}</span> : null}
    </span>
  )
}

/** The Change column: baseline → now for a scan row; for a watched row the
 *  values live in the expanded detail (B6), so the collapsed row names how many
 *  settings changed. A new or removed policy is the change itself. */
function ChangeSummary({ row, newPolicy, removedPolicy }: { row: UnifiedDriftRow } & RowCopy) {
  if (newPolicy) return <span className="text-text-secondary">New policy</span>
  if (removedPolicy) return <span className="text-text-secondary">Removed policy</span>
  if (row.source === 'DSC') {
    const raw = row.raw as { oldValue?: string; newValue?: string }
    return <ChangeCell from={raw.oldValue} to={raw.newValue} />
  }
  const n = (row.raw as TcmDriftSummary).driftedPropertyCount
  if (typeof n === 'number' && n > 0) {
    return (
      <span className="tabular-nums text-text-secondary">
        {n} {n === 1 ? 'setting' : 'settings'} changed
      </span>
    )
  }
  return <span className="text-text-helper">—</span>
}

/**
 * SeverityCell — severity as Carbon's glyph and word (only Critical takes a
 * hue). `provenance` (WO-5) is the "why is this High?" copy from
 * `composeSeverityProvenance`; null when the row has no signals to explain
 * itself, in which case no tooltip renders at all rather than an empty one.
 */
function SeverityCell({
  severity,
  provenance,
}: {
  severity: UnifiedDriftRow['severity']
  provenance?: string | null
}) {
  const level = driftSeverityLevel(severity)
  if (!severity || !level) {
    return <span className="text-text-helper">—</span>
  }
  const glyph = <SeverityGlyph level={level}>{severity}</SeverityGlyph>
  return provenance ? (
    <Tooltip label={provenance} definition align="start">
      {glyph}
    </Tooltip>
  ) : (
    glyph
  )
}

function worstSeverity(rows: UnifiedDriftRow[]): UnifiedDriftRow['severity'] {
  return rows.reduce((a, b) =>
    (SEVERITY_RANK[b.severity ?? 'Info'] ?? 9) < (SEVERITY_RANK[a.severity ?? 'Info'] ?? 9) ? b : a,
  ).severity
}

/** A resource group's header: its worst severity, the resource, how many settings drifted. */
function GroupHeader({ rows, upgradeLabel }: { rows: UnifiedDriftRow[]; upgradeLabel: string | null }) {
  const first = rows[0]
  const worst = worstSeverity(rows)
  const level = driftSeverityLevel(worst)
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
      {level ? <SeverityGlyph level={level}>{worst}</SeverityGlyph> : null}
      <span className="min-w-0 [overflow-wrap:anywhere]">
        {driftResourceName(first.resourceType)}
        {first.resourceName && first.resourceName !== first.resourceType ? (
          <span className="font-normal text-text-secondary"> › {first.resourceName}</span>
        ) : null}
      </span>
      <span className="text-helper font-normal tabular-nums text-text-helper">
        {rows.length} {rows.length === 1 ? 'setting' : 'settings'} drifted
      </span>
      {upgradeLabel ? <Tag kind="outline">{upgradeLabel}</Tag> : null}
    </span>
  )
}

/** A priority-shift group's header: its worst severity, the rules that moved, and what caused it. */
function PriorityShiftHeader({ rows, copy }: { rows: UnifiedDriftRow[]; copy: ReturnType<typeof priorityShiftCopy> }) {
  const worst = worstSeverity(rows)
  const level = driftSeverityLevel(worst)
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
      {level ? <SeverityGlyph level={level}>{worst}</SeverityGlyph> : null}
      <span className="min-w-0 [overflow-wrap:anywhere]">{copy.title}</span>
      <span className="text-helper font-normal text-text-helper">{copy.note}</span>
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Copy helpers                                                               */
/* -------------------------------------------------------------------------- */

// Verdict-voice notice title. Spells small counts ("Three changes staged")
// per the named-subject rule; falls back to the numeral past ten. No
// em-dashes — period-then-sentence reads cleaner.
const SMALL_NUMBER_WORDS = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five',
  'Six', 'Seven', 'Eight', 'Nine', 'Ten',
]

function stagedHeadline(count: number): string {
  const subject =
    count <= 10
      ? `${SMALL_NUMBER_WORDS[count]} change${count === 1 ? '' : 's'}`
      : `${count} changes`
  return `${subject} staged. Apply when you are ready.`
}

// "1 change" / "N changes" — the subject the toasts lead with.
function pluralizeChange(n: number): string {
  return n === 1 ? '1 change' : `${n} changes`
}

// Run-triage tally, in customer words. Leads with what Guardian read, then how
// much of it looks like routine churn (the only Phase 1 suggestion). No jargon,
// no em-dash. When nothing was read, says so plainly rather than "0 rows".
function triageRunSummary(scanned: number, churn: number): string {
  if (scanned === 0) {
    return 'Nothing new to read. The queue is already triaged.'
  }
  const read = `${scanned} ${scanned === 1 ? 'row' : 'rows'} read`
  if (churn === 0) {
    return `${read}. Nothing looks like routine churn.`
  }
  return `${read}. ${churn} ${churn === 1 ? 'row looks' : 'rows look'} like routine churn.`
}
